import VariantPicker from '@theme/variant-picker'
import { VariantUpdateEvent } from '@theme/events'

/**
 * A variant picker that displays a two-option product as a grid: the option
 * without swatches (e.g. Nib Size) becomes a heading, and the option with
 * swatches (e.g. Color) is rendered as swatches beneath it. Each swatch maps to
 * one real variant, so unavailable combinations simply aren't rendered.
 *
 * It reuses the native `<variant-picker>` behaviour (selection tracking, section
 * fetch, morph, events). Only two things differ:
 *   1. Each swatch carries BOTH option value ids (`data-option-values`), because a
 *      single click selects a full size+color pair rather than one option.
 *   2. The re-rendered JSON blob lives inside a `<grouped-variant-picker>` element,
 *      so the fetch handler looks for it under that tag name.
 *
 * Only supports single (non-combined-listing) products, which is the intended
 * refill use case.
 *
 * @extends VariantPicker
 */
export default class GroupedVariantPicker extends VariantPicker {
	/** @type {AbortController | undefined} */
	#sectionAbortController

	/**
	 * Returns the selected variant's option value ids (both axes), so the section
	 * request resolves the exact size+color variant rather than first-available.
	 * @returns {string[]}
	 */
	get selectedOptionsValues() {
		const checked = this.querySelector('fieldset input:checked')

		if (checked instanceof HTMLInputElement && checked.dataset.optionValues) {
			return checked.dataset.optionValues.split(',').filter(Boolean)
		}

		return super.selectedOptionsValues
	}

	/**
	 * Fetches the updated section and morphs the picker. Mirrors the base
	 * implementation but reads the variant JSON from the `grouped-variant-picker`
	 * tag and uses its own abort controller (the base one is private).
	 * @param {string} requestUrl
	 * @param {string} [morphElementSelector]
	 */
	fetchUpdatedSection(requestUrl, morphElementSelector) {
		this.#sectionAbortController?.abort()
		this.#sectionAbortController = new AbortController()

		fetch(requestUrl, { signal: this.#sectionAbortController.signal })
			.then(response => response.text())
			.then(responseText => {
				const html = new DOMParser().parseFromString(responseText, 'text/html')

				const textContent = html.querySelector(
					'grouped-variant-picker script[type="application/json"]'
				)?.textContent
				if (!textContent) return

				let newProduct

				if (morphElementSelector === 'main') {
					this.updateMain(html)
				} else if (morphElementSelector) {
					this.updateElement(html, morphElementSelector)
				} else {
					newProduct = this.updateVariantPicker(html)
				}

				if (this.selectedOptionId) {
					this.dispatchEvent(
						new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
							html,
							productId: this.dataset.productId ?? '',
							newProduct
						})
					)
				}
			})
			.catch(error => {
				if (error.name === 'AbortError') {
					console.warn('Fetch aborted by user')
				} else {
					console.error(error)
				}
			})
	}
}

if (!customElements.get('grouped-variant-picker')) {
	customElements.define('grouped-variant-picker', GroupedVariantPicker)
}
